// Pure behaviour rules for UnifiedContactSelector, split out of the component so
// they can be unit-tested (the component itself drags React + MUI + virtuoso,
// and jest here runs in a `node` environment with no DOM).

export interface ClearSearchDecisionInput {
    /** Selections render as removable chips above the list. */
    displaySelectedAsChips?: boolean;
    /** The caller accumulates selections — a pick is NOT terminal. */
    multiSelect?: boolean;
}

/**
 * Should the search box be emptied after the user picks a row from the list?
 *
 * Chip mode wants the tidy-up: once a pick becomes a chip, the query that found
 * it is spent, and leaving it behind puts a stale filter next to the fresh chip.
 *
 * But that is only true when a pick is TERMINAL. In multi-select the query IS the
 * user's work — they narrow to a family of contacts/groups and tick several out of
 * it — so clearing after each tick throws the narrowing away and forces a retype
 * per recipient (#160). There, the query has to survive.
 *
 * Note this governs the LIST-ROW path only. The free-input "+" add clears its own
 * query directly (you typed a raw number, it became a chip, the text is consumed),
 * which is the behaviour the original chip-mode clear was written to match.
 */
export const shouldClearSearchAfterSelect = ({
    displaySelectedAsChips,
    multiSelect,
}: ClearSearchDecisionInput): boolean => Boolean(displaySelectedAsChips) && !multiSelect;

export type PickerBodyState = 'loading' | 'free-row' | 'empty' | 'list';

export interface PickerBodyInput {
    /** The list is empty and a page is still being fetched. */
    isLoading: boolean;
    /** The list is empty and nothing is being fetched. */
    isEmpty: boolean;
    /** The search text is a number that can be offered as a free-input row. */
    freeRowAvailable: boolean;
    /** What the previous render showed (null on the first render). */
    lastShown: PickerBodyState | null;
}

/**
 * What the picker's list area shows.
 *
 * Every keystroke refetches the search, and while it does the list is empty and
 * "loading". Read naively, that put the spinner back over the free-input
 * "Not in your contacts" row (and the "enter the full number" hint) on every digit
 * typed after the search first answered — a flicker. Once a search has answered
 * with "no match", that answer is held through the next refetch; the row itself
 * follows the text as it is typed. Only a picker that has not answered yet shows
 * the spinner (a first search: the number may turn out to be a contact).
 */
export const pickerBodyState = ({ isLoading, isEmpty, freeRowAvailable, lastShown }: PickerBodyInput): PickerBodyState => {
    const noMatch = (): PickerBodyState => (freeRowAvailable ? 'free-row' : 'empty');
    if (isLoading) return lastShown === 'free-row' || lastShown === 'empty' ? noMatch() : 'loading';
    if (isEmpty) return noMatch();
    return 'list';
};
